/**
 * The markdown half of <RichEditor>: the extension list that decides what survives a
 * save, plus the small pure helpers around it (link href normalising, output tidy-up,
 * plain-text paste shaping). Kept free of React/DOM so `scripts/check-rich-editor-md.mjs`
 * can round-trip markdown through exactly the schema the editor uses.
 *
 * Anything the editor can hold but this list cannot serialise is silently dropped on
 * save — that is how GFM tables used to vanish. Add the extension here, not in the
 * component, and add a case to the check script.
 */
import type { AnyExtension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import { TaskItem, TaskList } from '@tiptap/extension-list';

/**
 * `@<email>` as stored — same shape `remarkMentions` in Markdown.tsx turns into a chip
 * (domain may be dotless, e.g. `dev@localhost`). The `@` must not follow a word char,
 * `@` or `.`, so `x@a@b.com` is not a mention.
 */
const MENTION_MD = /^@([\w.%+-]+@[\w-]+(?:\.[\w-]+)*)/;
const MENTION_START = /(^|[^\w@.])@[\w.%+-]+@[\w-]/;

/**
 * Mention ↔ `@<email>` markdown, both ways. Save: @tiptap/markdown reads
 * `renderMarkdown(node, helpers)` off the extension config (else it dumps the node's raw
 * attrs). Load: an inline tokenizer turns `@email` back into a mention node — without it
 * marked's GFM email autolink wins and a reloaded mention edits as a `mailto:` link.
 */
export const MentionMd = Mention.extend({
  renderMarkdown(node: { attrs: { id: string } }) {
    return `@${node.attrs.id}`;
  },
  markdownTokenName: 'mention',
  markdownTokenizer: {
    name: 'mention',
    level: 'inline',
    start: (src: string) => {
      const m = MENTION_START.exec(src);
      return m ? m.index + m[1].length : -1;
    },
    tokenize: (src: string, tokens: { raw?: string }[]) => {
      // marked splits text before an email, so `x@a@b.com` arrives here as `@a@b.com`
      if (/[\w@.]$/.test(tokens[tokens.length - 1]?.raw ?? '')) return undefined;
      const m = MENTION_MD.exec(src);
      return m ? { type: 'mention', raw: m[0], email: m[1] } : undefined;
    },
  },
  parseMarkdown: (token: { email?: string }, helpers: { createNode: (t: string, a: object) => unknown }) =>
    helpers.createNode('mention', { id: token.email, label: null }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any);

/**
 * Every extension that owns a piece of markdown syntax. `codeBlock` is passed in so the
 * editor can use its lowlight NodeView while the check script uses the plain node — both
 * serialise as a fenced block. `image` likewise: the editor passes `InfrasFile` (the same
 * `image` node with a NodeView that resolves `infras-file:<uuid>` attachments), so an
 * attachment saves exactly as `![name](infras-file:<uuid>)` — the plain Image's markdown.
 */
export function markdownExtensions(
  codeBlock: AnyExtension,
  mention: AnyExtension = MentionMd,
  image: AnyExtension = Image.configure({ inline: false }),
): AnyExtension[] {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: false,
      // StarterKit v3 bundles Underline, which saves as `++x++` — not GFM, and the
      // rendered view shows the plus signs. GitLab has no underline either.
      underline: false,
      link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
    }),
    codeBlock,
    TableKit.configure({ table: { resizable: false } }),
    TaskList,
    TaskItem.configure({ nested: true }),
    image,
    mention,
  ];
}

/** Outcome of normalising what the user typed into the link popover. */
export type HrefResult = { ok: true; href: string } | { ok: false; error: string };

/** `example.com` → `https://example.com`; only http(s) and mailto are accepted. */
export function normalizeHref(raw: string): HrefResult {
  const v = raw.trim();
  if (!v) return { ok: false, error: 'Enter a URL' };
  if (/^mailto:/i.test(v)) return { ok: true, href: v };
  if (/^https?:\/\//i.test(v)) return { ok: true, href: v };
  // Any other explicit scheme (javascript:, data:, ftp:, file:) is refused.
  if (/^[a-z][a-z0-9+.-]*:/i.test(v) && !/^[^:/]+:\d+(\/|$)/.test(v)) {
    return { ok: false, error: 'Only http(s) and mailto links are allowed' };
  }
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(v)) return { ok: true, href: `mailto:${v}` };
  if (/\s/.test(v)) return { ok: false, error: 'A URL cannot contain spaces' };
  return { ok: true, href: `https://${v.replace(/^\/\//, '')}` };
}

const FENCE = /^(```|~~~)/;
/** Private-use marker for "escape here"; a literal `\` in text would itself be escaped. */
const ESC = '\uE000';

/**
 * Tidy the serialised markdown: drop leading/trailing blank lines and repeated blank
 * lines, turn `[url](url)` (what an autolinked bare URL serialises to) back into the
 * bare URL, which GFM autolinks on render, and resolve the escape markers placed by
 * `escapeBlockStarts`. Escapes the serializer adds where CommonMark could never read
 * emphasis are dropped (`snake\_case`, `2 \* 3`), and GFM table cell padding is
 * compacted (`| a | b |`). Lines inside fenced code blocks and inline code are left alone.
 * C0 control characters other than \n and \t never reach the stored text: U+001F is how
 * the serializer joins two paragraphs inside a table cell (it becomes a space), the rest
 * are dropped.
 */
export function tidyMarkdown(md: string): string {
  let inFence = false;
  const out: string[] = [];
  // eslint-disable-next-line no-control-regex
  const clean = md.replace(/\u001f/g, ' ').replace(/[\u0000-\u0008\u000b-\u001f]/g, '');
  for (const raw of indentListChildren(clean.split('\n'))) {
    const line = raw.split(ESC).join('\\');
    if (FENCE.test(line.trimStart())) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }
    if (!line.trim() && out.length && !out[out.length - 1].trim()) continue;
    out.push(outsideCodeSpans(line, unescapeInert).replace(/\[(https?:\/\/[^\]\s]+)\]\(\1\)/g, '$1'));
  }
  return compactTables(out)
    .join('\n')
    .replace(/^(?:[ \t]*\n)+/, '')
    .replace(/(?:\n[ \t]*(?:&nbsp;|\u00a0)?[ \t]*)+$/, '')
    .replace(/\s+$/, '');
}

/**
 * Drop escapes that guard nothing. `_` between two letters/digits cannot open or close
 * emphasis, and a `*` with whitespace on both sides is neither left- nor right-flanking
 * (CommonMark 6.2). The `*` must have non-space text before it on the line, so an
 * unescaped one can never become a list marker or a thematic break.
 */
function unescapeInert(text: string): string {
  return text
    .replace(/(?<=[\p{L}\p{N}])\\_(?=[\p{L}\p{N}])/gu, '_')
    .replace(/(?<=\S[ \t]+)\\\*(?=[ \t])/g, '*');
}

/** Apply `fn` to the parts of `line` outside inline code spans (backtick runs of equal length). */
function outsideCodeSpans(line: string, fn: (s: string) => string): string {
  let out = '';
  let i = 0;
  let plain = '';
  while (i < line.length) {
    if (line[i] === '`' && line[i - 1] !== '\\') {
      const run = /^`+/.exec(line.slice(i))![0];
      const close = new RegExp(`(?<!\`)${run}(?!\`)`).exec(line.slice(i + run.length));
      if (close) {
        const end = i + run.length + close.index + run.length;
        out += fn(plain) + line.slice(i, end);
        plain = '';
        i = end;
        continue;
      }
      plain += run;
      i += run.length;
      continue;
    }
    plain += line[i++];
  }
  return out + fn(plain);
}

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_DELIM = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;

/** `| a   | b   |` / `| ----- |` → `| a | b |` / `| --- |`, keeping alignment colons. */
function compactTables(lines: string[]): string[] {
  const out = [...lines];
  const cellsOf = (l: string) => l.trim().slice(1, -1).split(/(?<!\\)\|/).map((c) => c.trim());
  let width = 0;
  const row = (l: string, delim: boolean) => {
    const lead = /^\s*/.exec(l)![0];
    const cells = cellsOf(l);
    // a row whose cells we can't count reliably (e.g. a `|` inside inline code) stays as-is
    if (cells.length !== width) return l;
    const fixed = delim
      ? cells.map((c) => `${c.startsWith(':') ? ':' : ''}---${c.endsWith(':') ? ':' : ''}`)
      : cells;
    return `${lead}| ${fixed.join(' | ')} |`;
  };
  let inFence = false;
  for (let i = 1; i < out.length; i++) {
    if (FENCE.test(out[i - 1].trimStart())) inFence = !inFence;
    if (inFence || !TABLE_DELIM.test(out[i]) || !TABLE_ROW.test(out[i - 1])) continue;
    width = cellsOf(out[i]).length;
    out[i - 1] = row(out[i - 1], false);
    out[i] = row(out[i], true);
    for (i++; i < out.length && TABLE_ROW.test(out[i]); i++) out[i] = row(out[i], false);
  }
  return out;
}

const LIST_ITEM = /^([-+*]|\d{1,9}[.)])( +|$)/;

/**
 * The serializer nests every list child 2 spaces under its parent item. That is right
 * for `- `, but a child of `1. ` needs 3 (`10. ` needs 4) — CommonMark requires the
 * marker width — or the renderer lifts it out as a separate top-level list. Re-indent
 * each child to its parent's content column; fenced code inside an item shifts with it.
 */
function indentListChildren(lines: string[]): string[] {
  // open list items: serializer indent of the item, and its children's column in the output
  const stack: { at: number; childAt: number }[] = [];
  let fence: { shift: number } | null = null;
  return lines.map((line) => {
    const lead = /^ */.exec(line)![0].length;
    const body = line.slice(lead);
    if (fence) {
      if (FENCE.test(body)) {
        const shift = fence.shift;
        fence = null;
        return shift > 0 ? ' '.repeat(shift) + line : line;
      }
      return fence.shift > 0 && line ? ' '.repeat(fence.shift) + line : line;
    }
    if (!body) return line;
    while (stack.length && stack[stack.length - 1].at >= lead) stack.pop();
    const parent = stack[stack.length - 1];
    const indent = parent && lead >= parent.at + 2 ? parent.childAt + (lead - parent.at - 2) : lead;
    const m = LIST_ITEM.exec(body);
    if (m) stack.push({ at: lead, childAt: indent + m[1].length + 1 });
    if (FENCE.test(body)) fence = { shift: indent - lead };
    return indent === lead ? line : ' '.repeat(indent) + body;
  });
}

/**
 * Load-side counterpart of `indentListChildren`. @tiptap/extension-list strips only
 * `digits + 1` columns from an ordered item's child lines (it leaves the `.`/`)` out of
 * the marker width), and then trims the first block line — so a fence correctly nested
 * at 3 spaces under `1. ` reloads with one extra space on every code line, growing each
 * save. Pull fenced code whose nearest list item is ordered back by one column before
 * parsing; nested lists and paragraphs tolerate the extra space and are left alone.
 * Stored markdown stays CommonMark — this only feeds the editor's parser.
 */
export function prepareMarkdown(md: string): string {
  const stack: { at: number; ordered: boolean }[] = [];
  let fence = false;
  let shift = 0;
  return md
    .split('\n')
    .map((line) => {
      const lead = /^ */.exec(line)![0].length;
      const body = line.slice(lead);
      const cut = () => (shift && lead >= shift ? line.slice(shift) : line);
      if (fence) {
        if (FENCE.test(body)) fence = false;
        return cut();
      }
      if (!body) return line;
      while (stack.length && stack[stack.length - 1].at >= lead) stack.pop();
      const m = LIST_ITEM.exec(body);
      if (m) {
        stack.push({ at: lead, ordered: /\d/.test(m[1]) });
        return line;
      }
      if (FENCE.test(body)) {
        const parent = stack[stack.length - 1];
        fence = true;
        shift = parent?.ordered && lead > parent.at ? 1 : 0;
        return cut();
      }
      return line;
    })
    .join('\n');
}

type JsonNode = { type?: string; text?: string; content?: JsonNode[]; [k: string]: unknown };

/**
 * Escape, on the JSON before serialising, what the serializer writes verbatim but the
 * parser would read as syntax:
 * - a paragraph whose text starts with `# `, `- `, `* `, `+ ` or `1. ` (the serializer
 *   escapes only `>`, as `&gt;`) reloads as a heading, list or quote — escape that one
 *   leading marker;
 * - a `|` in a table cell (plain text or inline code) ends the cell and splits the row —
 *   write it as `\|`, which GFM reads back as `|` in both.
 */
export function escapeBlockStarts<T extends JsonNode>(node: T, inCell = false): T {
  if (node.type === 'codeBlock') return node;
  const cell = inCell || node.type === 'tableCell' || node.type === 'tableHeader';
  if (node.type === 'text') {
    return cell && node.text?.includes('|') ? { ...node, text: node.text.split('|').join(`${ESC}|`) } : node;
  }
  if (!node.content) return node;
  const content = node.content.map((c) => escapeBlockStarts(c, cell));
  const first = content[0];
  if (node.type === 'paragraph' && first?.type === 'text') {
    const t = first.text ?? '';
    let esc = t;
    if (/^(?:#{1,6}|[-+*])(?:\s|$)/.test(t)) esc = `${ESC}${t}`;
    else if (/^\d{1,9}[.)](?:\s|$)/.test(t)) esc = t.replace(/^(\d+)([.)])/, `$1${ESC}$2`);
    if (esc !== t) content[0] = { ...first, text: esc };
  }
  return { ...node, content };
}

/** Shape of a plain-text paste: one code block, or paragraphs whose lines are hard breaks. */
export type PastePlan = { kind: 'code'; text: string } | { kind: 'paragraphs'; paragraphs: string[][] };

const CODE_LINE =
  /[;{}]\s*$|^\s*(?:import|export|const|let|var|function|def|class|return|if|for|while|public|private|package|#include|SELECT|FROM|WHERE)\b|=>|^\s*(?:\/\/|#!)/;

/**
 * Decide how multi-line plain text (no HTML on the clipboard) lands in the editor.
 * Prose keeps its line structure — blank-line-separated paragraphs, single newlines as
 * hard breaks — instead of becoming one paragraph per line; common indentation is
 * stripped so indented prose does not look like code. It becomes a code block only
 * when it clearly is code: at least half of its non-empty lines look like code.
 */
export function planPlainTextPaste(text: string): PastePlan {
  const lines = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n');
  const filled = lines.filter((l) => l.trim());
  const codeish = filled.filter((l) => CODE_LINE.test(l)).length;
  if (filled.length >= 2 && codeish * 2 >= filled.length) {
    return { kind: 'code', text: dedent(lines).join('\n') };
  }
  const paragraphs: string[][] = [];
  let cur: string[] = [];
  for (const l of dedent(lines)) {
    if (!l.trim()) {
      if (cur.length) paragraphs.push(cur);
      cur = [];
    } else cur.push(l.trimEnd());
  }
  if (cur.length) paragraphs.push(cur);
  return { kind: 'paragraphs', paragraphs };
}

function dedent(lines: string[]): string[] {
  const indents = lines.filter((l) => l.trim()).map((l) => (/^[ \t]*/.exec(l)?.[0].length ?? 0));
  const min = indents.length ? Math.min(...indents) : 0;
  return min ? lines.map((l) => l.slice(Math.min(min, /^[ \t]*/.exec(l)?.[0].length ?? 0))) : lines;
}
